import { RemoteDepositBatch, RemoteDepositLocation } from '@treasury/domain/remote-deposit-capture';
import { checkmarkIcon, circleCloseIcon, clockIcon } from '@treasury/presentation/assets/icons';
import { format } from 'date-fns';
import { type TemplateResult } from 'lit';
import { RemoteDepositItemVm } from './remote-deposit-item.vm';

enum DepositStatus {
    Deleted = 'BatchStatus01',
    Deposited = 'BatchStatus02',
    DepositedWithAdjustment = 'BatchStatus03',
    NeedsRescan = 'BatchStatus04',
    OpenForScanning = 'BatchStatus05',
    PartialDeposit = 'BatchStatus06',
    Rejected = 'BatchStatus07',
    Submitted = 'BatchStatus08',
    Unknown = 'BatchStatus09',
}

export class DepositVm extends RemoteDepositBatch {
    constructor(batch: RemoteDepositBatch, location?: RemoteDepositLocation) {
        super(batch.toDto(), location);
        this.checks = batch.checks?.map(check => new RemoteDepositItemVm(check)) ?? [];
    }

    public checks: RemoteDepositItemVm[] = [];

    public get totalCount() {
        return this.checks?.length || this.dto.totalCount;
    }

    public get statusColor() {
        return this.getStatusMetadata().color;
    }

    public get statusIcon() {
        return this.getStatusMetadata().statusIcon;
    }

    public get canDeleteDeposit() {
        // This is identical logic as isOpen, but it is more expressive to have a separate property in case the logic needs tweaking
        return this.dto.status === DepositStatus.OpenForScanning;
    }

    public get canSubmitDeposit() {
        return this.dto.status === DepositStatus.OpenForScanning && this.checks?.length > 0;
    }

    public get isOpen() {
        return this.dto.status === DepositStatus.OpenForScanning;
    }

    public addCheck(newCheck: RemoteDepositItemVm) {
        if (!this.checks) {
            this.checks = [];
        }
        this.checks.push(newCheck);
        this.dto.totalAmount = this.checks.reduce((acc, check) => acc + check.amount, 0);
    }

    public deleteCheck(itemReference: string) {
        this.checks = this.checks?.filter(check => check.itemReference !== itemReference);
        this.dto.totalAmount = this.checks?.reduce((acc, check) => acc + check.amount, 0) ?? 0;
    }

    public get formattedTotalAmount() {
        const formatCurrency = (amount: number) =>
            amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            });

        return this.dto.totalAmount ? formatCurrency(this.dto.totalAmount) : formatCurrency(0);
    }

    public get formattedDepositDate() {
        return format(this.date, 'iiii, MMMM d, yyyy');
    }

    private getStatusMetadata() {
        let statusIcon: TemplateResult<2>;
        let color: string;
        switch (this.dto.statusDescription) {
            case 'Deposited':
            case 'Deposited With Adjustment':
            case 'Partial Deposit':
                statusIcon = checkmarkIcon;
                color = 'green';
                break;
            case 'Rejected':
            case 'Deleted':
                statusIcon = circleCloseIcon;
                color = 'red';
                break;
            default:
                statusIcon = clockIcon;
                color = 'orange';
        }
        return { statusIcon, color };
    }
}
